import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import cn from 'classnames';
import { ProfileContext } from 'context';
import { useAction } from 'hooks';
import App, { AlertsBus } from 'modules/App';
import Account from 'modules/Account';
import { HcpDownloadDataModal } from 'modules/Meter';
import Notifications, { ConfirmDismissFilteredNotificationsAlert } from 'modules/Notifications';
import ClinicManagement from 'modules/ClinicManagement';
import Hcp, { DeclineEnrollModal, InvitePatientModal, MergePatientsModal, RemovePatientModal } from 'modules/Hcp';
import Visit from 'modules/Visit';
import * as actions from '../../actions';
import * as constants from '../../constants';
import * as selectors from '../../selectors';
import { useIsPinnedMenu } from '../../hooks';
import MainLayout from '../../partials/MainLayout';
import HcpTopBar from './HcpTopBar';
import HcpSidebar from './HcpSidebar';
import ClinicInfo from './ClinicInfo';
import ClinicSetLicence from './ClinicSetLicence';
import ClosePrintModeBtn from './ClosePrintModeBtn';
import styles from './MainLayout.pcss';
const profileContext = {
    selectors: {
        activePatient: Hcp.selectors.activePatient,
        phiSet: Hcp.selectors.phiSet,
        prvKeyPem: Account.selectors.activeClinicMembershipPrvKeyPem,
        passphrase: Account.selectors.activeClinicMembershipPassphrase,
        phiSetDocumentId: Hcp.selectors.phiSetDocumentId,
        readings: Hcp.selectors.readings,
        cgmReadings: Hcp.selectors.cgmReadings,
        highlightedReadings: Hcp.selectors.highlightedReadings,
        measurements: Hcp.selectors.measurements,
        measurementsBatchesIndex: Hcp.selectors.measurementsBatchesIndex,
        notes: Hcp.selectors.notes,
        relatedData: Hcp.selectors.relatedData,
        timeSeriesResources: Hcp.selectors.timeSeriesResources,
        clinicSettings: ClinicManagement.selectors.clinicSettings,
        activeVisit: Visit.selectors.activeVisit,
        visitHistoryActiveVisit: Visit.selectors.activeVisit
    },
    actions: {
        setReadings: Hcp.actions.setReadings,
        storeReadingsSuccess: Hcp.actions.setImportedReadings
    }
};
const useContent = (children)=>{
    const activeClinicMembership = useSelector(Account.selectors.activeClinicMembership);
    const activeOrganizationMembership = useSelector(Account.selectors.activeOrganizationMembership);
    if (activeOrganizationMembership && activeOrganizationMembership.status === 'Pending') {
        return /*#__PURE__*/ _jsx(ClinicInfo, {
            isMembershipPending: true,
            clinic: activeOrganizationMembership.organization
        });
    }
    if (activeOrganizationMembership && activeOrganizationMembership.organization.status === 'Pending') {
        return /*#__PURE__*/ _jsx(ClinicInfo, {
            isClinicPending: true,
            clinic: activeOrganizationMembership.organization
        });
    }
    if (activeClinicMembership && !activeClinicMembership.clinic.storageAccount && !activeClinicMembership.isAdmin) {
        return /*#__PURE__*/ _jsx(ClinicInfo, {
            isClinicNotConfigured: true,
            clinic: activeClinicMembership.clinic
        });
    }
    if (activeClinicMembership && activeClinicMembership.clinic.clinicStatus === 'WaitingForLicence') {
        return /*#__PURE__*/ _jsx(ClinicSetLicence, {
            activeClinicMembership: activeClinicMembership
        });
    }
    return children;
};
const HcpMainLayout = ({ metaTitleMessage, metaDescriptionMessage, children })=>{
    const content = useContent(children);
    const isPinnedMenu = useIsPinnedMenu();
    const menuOpen = useSelector(selectors.menuOpen);
    const selectedMenuItem = useSelector(selectors.selectedMenuItem);
    const windowWidth = useSelector(selectors.windowWidth);
    const direction = useSelector(App.selectors.direction);
    const visitNotesFloatingModal = useSelector(App.selectors.floatingModalSelector(Visit.constants.VISIT_NOTES_FM));
    const isPrintMode = useSelector(App.selectors.printMode);
    const activeClinicMembership = useSelector(Account.selectors.activeClinicMembership);
    const lastReceivedNotification = useSelector(Notifications.selectors.lastReceivedNotification);
    const clinicMemberships = useSelector(ClinicManagement.selectors.clinicMemberships);
    const patients = useSelector(Hcp.selectors.patients);
    const activeVisit = useSelector(Visit.selectors.activeVisit);
    const setMenuOpen = useAction(actions.setMenuOpen);
    const setSelectedMenuItem = useAction(actions.setSelectedMenuItem);
    const closeDropdown = useAction(App.actions.closeDropdown);
    const checkPendingMemberships = useAction(ClinicManagement.actions.checkPendingOrganizationMemberships);
    const checkPatientsWithoutLeadingHcp = useAction(ClinicManagement.actions.checkPatientsWithoutLeadingHcp);
    const fetchClinicMemberships = useAction(ClinicManagement.actions.fetchClinicMemberships);
    useEffect(()=>{
        if (activeClinicMembership && activeClinicMembership.isAdmin && activeClinicMembership.passphrase) {
            checkPendingMemberships();
            fetchClinicMemberships(activeClinicMembership.clinicId);
        }
    }, [
        activeClinicMembership
    ]);
    useEffect(()=>{
        if (activeClinicMembership && activeClinicMembership.isAdmin && clinicMemberships.length) {
            checkPatientsWithoutLeadingHcp();
        }
    }, [
        clinicMemberships,
        patients
    ]);
    useEffect(()=>{
        const { CLINIC_MEMBERSHIP_APPROVED } = Notifications.constants.NOTIFICATIONS_TRIGGERS;
        if (lastReceivedNotification && lastReceivedNotification.notificationTrigger === CLINIC_MEMBERSHIP_APPROVED) {
            window.location.reload();
        }
    }, [
        lastReceivedNotification
    ]);
    useEffect(()=>{
        if (visitNotesFloatingModal && windowWidth < constants.PINNED_MENU_NOTES_BREAKPOINT) {
            setMenuOpen(false);
        }
    }, [
        visitNotesFloatingModal
    ]);
    const onClickSidebar = (evt)=>{
        evt.stopPropagation();
        closeDropdown();
    };
    return /*#__PURE__*/ _jsx(MainLayout, {
        metaTitleMessage: metaTitleMessage,
        metaDescriptionMessage: metaDescriptionMessage,
        children: /*#__PURE__*/ _jsxs(ProfileContext.Provider, {
            value: profileContext,
            children: [
                /*#__PURE__*/ _jsxs("div", {
                    className: cn({
                        [styles.container]: !isPrintMode,
                        [styles.layoutContainer]: !isPrintMode
                    }),
                    children: [
                        /*#__PURE__*/ _jsx(HcpTopBar, {}),
                        /*#__PURE__*/ _jsxs("div", {
                            className: cn({
                                [styles.layout]: !isPrintMode
                            }),
                            children: [
                                /*#__PURE__*/ _jsxs("div", {
                                    className: cn({
                                        'row no-gutters': !isPrintMode,
                                        [styles.sidebarContainer]: !isPrintMode
                                    }),
                                    children: [
                                        /*#__PURE__*/ _jsx("div", {
                                            className: cn(styles.sidebarHolder, {
                                                [styles['sidebarHolder--extended']]: isPinnedMenu
                                            }),
                                            onClick: onClickSidebar,
                                            onKeyDown: undefined,
                                            children: /*#__PURE__*/ _jsx("div", {
                                                className: cn('col-auto d-print-none', {
                                                    [styles.hide]: isPrintMode
                                                }),
                                                children: /*#__PURE__*/ _jsx(HcpSidebar, {
                                                    direction: direction,
                                                    menuOpen: menuOpen,
                                                    selectedMenuItem: selectedMenuItem,
                                                    activeVisit: activeVisit,
                                                    isPinnedMenu: isPinnedMenu,
                                                    setMenuOpen: setMenuOpen,
                                                    setSelectedMenuItem: setSelectedMenuItem
                                                })
                                            })
                                        }),
                                        /*#__PURE__*/ _jsx(ClosePrintModeBtn, {})
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "content",
                                    children: [
                                        /*#__PURE__*/ _jsx(AlertsBus, {
                                            className: "px-6",
                                            isGlobal: true,
                                            activeClinicMembership: activeClinicMembership
                                        }),
                                        /*#__PURE__*/ _jsx(ConfirmDismissFilteredNotificationsAlert, {}),
                                        content
                                    ]
                                })
                            ]
                        })
                    ]
                }),
                /*#__PURE__*/ _jsx(HcpDownloadDataModal, {}),
                /*#__PURE__*/ _jsx(MergePatientsModal, {}),
                /*#__PURE__*/ _jsx(DeclineEnrollModal, {}),
                /*#__PURE__*/ _jsx(InvitePatientModal, {}),
                /*#__PURE__*/ _jsx(RemovePatientModal, {})
            ]
        })
    });
};
export default HcpMainLayout;
